import Dialog from 'components/ReusableComponents/Dialog'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import styles from './SavibookPreview.module.scss'
import {
  getSavibookPreviewCopy,
  RESERVATION_PURPOSE_OPTIONS
} from './savibookPreviewCopy'

const HERO_IMAGE = `${process.env.PUBLIC_URL}/ota-buster-hero.jpg`

const PREVIEW_SCREENS = {
  form: 'form',
  success: 'success'
}

function PreviewField({ label, children }) {
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      {children}
    </div>
  )
}

PreviewField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}

function SavibookPreviewModal({ step, hotelName, firstName, onClose }) {
  const [screen, setScreen] = useState(PREVIEW_SCREENS.form)

  const copy = getSavibookPreviewCopy(step.id, { firstName, hotelName })

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <Dialog>
      <div
        className={styles.shell}
        role="dialog"
        aria-modal="true"
        aria-label={`Savibook preview — ${step.title}`}
        data-testid="savibook-preview-modal"
      >
        <div className={styles.topBar}>
          <div className={styles.topTitles}>
            <span className={styles.topEyebrow}>Savibook preview</span>
            <h2 className={styles.topTitle}>{step.title}</h2>
          </div>
          <div className={styles.topActions}>
            <div className={styles.stateToggle} role="group" aria-label="Preview state">
              <button
                type="button"
                className={[
                  styles.stateButton,
                  screen === PREVIEW_SCREENS.form ? styles.stateButtonActive : ''
                ].join(' ')}
                onClick={() => setScreen(PREVIEW_SCREENS.form)}
              >
                Landing
              </button>
              <button
                type="button"
                className={[
                  styles.stateButton,
                  screen === PREVIEW_SCREENS.success ? styles.stateButtonActive : ''
                ].join(' ')}
                onClick={() => setScreen(PREVIEW_SCREENS.success)}
              >
                Success
              </button>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close preview"
            >
              ×
            </button>
          </div>
        </div>

        <div className={styles.stage}>
          <div className={styles.phone}>
            <div className={styles.page}>
              <div
                className={styles.hero}
                style={{
                  backgroundImage: `linear-gradient(180deg, rgba(12, 24, 48, 0.55) 0%, rgba(12, 24, 48, 0.75) 100%), url(${HERO_IMAGE})`
                }}
              >
                <div className={styles.brandRow}>
                  <div className={styles.hotelName}>{hotelName}</div>
                </div>

                {screen === PREVIEW_SCREENS.form && (
                  <>
                    <h1 className={styles.header}>{copy.header}</h1>
                    <p className={styles.body}>{copy.body}</p>
                  </>
                )}
              </div>

              <div className={styles.panel}>
                {screen === PREVIEW_SCREENS.form && (
                  <div className={styles.form}>
                    <p className={styles.manualHint}>
                      Please confirm your details below so we can get your stay ready.
                    </p>

                    {copy.showStayRating && (
                      <PreviewField label="Rate your stay">
                        <div className={styles.ratingRow} aria-hidden="true">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span key={star} className={styles.star}>
                              ★
                            </span>
                          ))}
                        </div>
                      </PreviewField>
                    )}

                    <PreviewField label="Full name">
                      <div className={styles.input}>
                        <span className={styles.placeholder}>Full name</span>
                      </div>
                    </PreviewField>

                    <PreviewField label="Reservation purpose">
                      <div className={[styles.input, styles.selectInput].join(' ')}>
                        <span className={styles.placeholder}>Select option</span>
                        <span className={styles.caret} aria-hidden="true">
                          ▼
                        </span>
                      </div>
                    </PreviewField>

                    <PreviewField label="Government ID">
                      <div className={styles.input}>
                        <span className={styles.placeholder}>ID number (optional)</span>
                      </div>
                    </PreviewField>

                    <PreviewField label="Email address">
                      <div className={styles.input}>
                        <span className={styles.placeholder}>Email address</span>
                      </div>
                    </PreviewField>

                    <PreviewField label="Phone number">
                      <div className={styles.input}>
                        <span className={styles.placeholder}>( ___ ) ___ - ____</span>
                      </div>
                    </PreviewField>

                    <div className={styles.cta}>{copy.cta}</div>

                    <div className={styles.consent}>
                      <span className={styles.consentBox} aria-hidden="true" />
                      <span className={styles.consentLabel}>
                        Keep me posted with offers and updates
                        {hotelName ? ` from ${hotelName}` : ''}. You can unsubscribe at any
                        time.
                      </span>
                    </div>

                    <p className={styles.legalText}>
                      By continuing you consent to the hotel contacting you about your
                      stay using the details you provide.
                    </p>
                  </div>
                )}

                {screen === PREVIEW_SCREENS.success && (
                  <div className={styles.success}>
                    <div className={styles.successIcon} aria-hidden="true">
                      ✓
                    </div>
                    <h2 className={styles.successTitle}>{copy.successTitle}</h2>
                    <p className={styles.successBody}>{copy.successBody}</p>
                    {copy.successClosing && (
                      <p className={styles.successClosing}>{copy.successClosing}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <p className={styles.footNote}>
          Visual preview only — nothing is sent and no guest data is captured. Reservation
          purpose options: {RESERVATION_PURPOSE_OPTIONS.join(', ')}.
        </p>
      </div>
    </Dialog>
  )
}

SavibookPreviewModal.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  }).isRequired,
  hotelName: PropTypes.string,
  firstName: PropTypes.string,
  onClose: PropTypes.func.isRequired
}

export default SavibookPreviewModal
